import React, { useState, useEffect, useRef } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";

import {
    CertificateStickerPlaceholder,
    ThumbnailsPlaceholder,
} from "../Placeholders";
import { CircularProgressCustom } from "../CircularProgressCustom";
import { apiUrl } from "../../../../services/constants";
import {
    GET_DATA_FOR_COLLECTION_SPEC_SECTION,
    GET_COLLECTION_CERTIFICATES,
    GET_COLLECTION_IMAGE,
    GET_COLLECTION_THUMBNAILS,
} from "../../../../services/graphql-query";

function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

export default function QuickView({ collectionId }) {
    const params = useParams();
    const [productPhotoUrl, setProductPhotoUrl] = useState(null);
    const [thumbnails, setThumbnails] = useState([]);
    const [certificates, setCertificates] = useState([]);
    const [isChangedPhoto, setIsChangedPhoto] = useState(false);
    const prevProductPhotoUrl = usePrevious(productPhotoUrl);

    const { data: productImageData } = useQuery(GET_COLLECTION_IMAGE, {
        variables: { id: collectionId },
        onCompleted: (res) => {
            setProductPhotoUrl(res.collection.data.attributes.image.data);
        },
    });

    const {
        loading: certificatesLoading,
        error: certificatesError,
        data: certificatesData,
    } = useQuery(GET_COLLECTION_CERTIFICATES, {
        onCompleted: (res) => {
            setCertificates(res.certificates.data);
        },
    });

    const {
        loading: thumbnailLoading,
        error: thumbnailError,
        data: thumbnailsData,
    } = useQuery(GET_COLLECTION_THUMBNAILS, {
        variables: { productId: collectionId },
        onCompleted: (res) => {
            setThumbnails(res.collection.data.attributes.thumbnails.data);
        },
    });

    const {
        loading: specificationLoading,
        error: specificationError,
        data: specificationData,
    } = useQuery(GET_DATA_FOR_COLLECTION_SPEC_SECTION, {
        variables: { productId: collectionId },
    });

    useEffect(() => {
        if (productPhotoUrl && isChangedPhoto) {
            const replacementItem = thumbnails.findIndex((t) => {
                return t.id == productPhotoUrl.id;
            });
            const newArray = [...thumbnails];
            newArray.splice(replacementItem, 1, prevProductPhotoUrl);
            setThumbnails(newArray);
            setIsChangedPhoto(false);
        }
    }, [productPhotoUrl, isChangedPhoto]);

    return (
        <div className="row">
            <div className="col-xl-6 mb-xl-0 mb-3">
                <div className="d-md-flex align-items-center justify-content-center">
                    <div className="d-flex flex-column col-md-8 ">
                        <h2 className="text-center">{params.collectionName}</h2>

                        <div
                            className="m-2 d-flex align-items-center justify-content-center"
                            style={{ height: "300" }}
                        >
                            {/* <div style={{ maxHeight: "300" }} className="w-100"> */}
                            <LazyLoadImage
                                src={
                                    productPhotoUrl &&
                                    apiUrl + productPhotoUrl.attributes.url
                                }
                                style={{
                                    objectFit: "contain",
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                }}
                                effect="opacity"
                            />
                            {/* </div> */}
                        </div>
                        <div className="d-flex flex-wrap justify-content-center align-items-center my-2">
                            {certificatesLoading ? (
                                Array.from({ length: 7 }, (v, k) => (
                                    <CertificateStickerPlaceholder key={k} />
                                ))
                            ) : certificatesError ? (
                                <p>Unable to download certificates</p>
                            ) : (
                                certificates.map((cert) => {
                                    return (
                                        <div
                                            className="d-flex justify-content-center align-items-center placeholder-glow"
                                            key={cert.id}
                                        >
                                            <LazyLoadImage
                                                src={
                                                    apiUrl +
                                                    cert.attributes.image.data
                                                        .attributes.url
                                                }
                                                width={50}
                                                height="auto"
                                                alt={cert.attributes.title}
                                                effect="opacity"
                                            />
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                    <div className="d-flex flex-wrap justify-content-center placeholder-glow">
                        {thumbnailLoading ? (
                            Array.from({ length: 5 }, (v, k) => (
                                <ThumbnailsPlaceholder key={k} />
                            ))
                        ) : thumbnailError ? (
                            <div
                                style={{
                                    transform: "rotate(-90deg)",
                                }}
                            >
                                <p className="text-center">
                                    Unable to download thumbnails
                                </p>
                            </div>
                        ) : (
                            thumbnails.map((thumb) => {
                                return (
                                    <div key={thumb.id} className="m-1">
                                        <LazyLoadImage
                                            src={apiUrl + thumb.attributes.url}
                                            alt={thumb.attributes.name}
                                            width={70}
                                            height={70}
                                            className="border"
                                            style={{
                                                cursor: "pointer",
                                            }}
                                            effect="opacity"
                                            onClick={() => {
                                                setProductPhotoUrl(thumb);
                                                setIsChangedPhoto(true);
                                            }}
                                        />
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
            <div className="col-xl-6">
                <div className="d-flex flex-column justify-content-center h-100">
                    <div>
                        <h2 className="text-uppercase">Specifications</h2>
                        <h2 className="fw-bold">{params.collectionName}</h2>
                    </div>
                    <div className="d-flex justify-content-center align-items-center h-100">
                        {specificationLoading ? (
                            <CircularProgressCustom />
                        ) : specificationError ? (
                            <p className="lead">Oops. Something went wrong</p>
                        ) : (
                            <div className="row row-cols-2 w-100 h-100 ">
                                <div
                                    className="col d-flex justify-content-center align-items-center"
                                    style={{
                                        maxHeight: "50%",
                                        height: "50%",
                                    }}
                                >
                                    <LazyLoadImage
                                        src={
                                            apiUrl +
                                            specificationData.collection.data
                                                .attributes.lumene.data
                                                ?.attributes.url
                                        }
                                        effect="opacity"
                                    />
                                </div>
                                <div
                                    className="col d-flex justify-content-center align-items-center"
                                    style={{
                                        maxHeight: "50%",
                                        height: "50%",
                                    }}
                                >
                                    <LazyLoadImage
                                        src={
                                            apiUrl +
                                            specificationData.collection.data
                                                .attributes.standard_cct.data
                                                ?.attributes.url
                                        }
                                        effect="opacity"
                                    />
                                </div>
                                <div
                                    className="col d-flex justify-content-center align-items-center"
                                    style={{
                                        maxHeight: "50%",
                                        height: "50%",
                                    }}
                                >
                                    <LazyLoadImage
                                        src={
                                            apiUrl +
                                            specificationData.collection.data
                                                .attributes.wattage.data
                                                ?.attributes.url
                                        }
                                        effect="opacity"
                                    />
                                </div>
                                <div
                                    className="col d-flex justify-content-center align-items-center"
                                    style={{
                                        maxHeight: "50%",
                                        height: "50%",
                                    }}
                                >
                                    <LazyLoadImage
                                        src={
                                            apiUrl +
                                            specificationData.collection.data
                                                .attributes.optics.data
                                                ?.attributes.url
                                        }
                                        effect="opacity"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
